import type { UserSpace, UserStoreCookie } from "~/constants/cookies";
import Role from "~/types/Role";

export function getAvailableSpaces(roles: string[] = []): Exclude<UserSpace, null>[] {
  const spaces: Exclude<UserSpace, null>[] = [];
  const hasAuditorRole = roles.includes(Role.AUDITOR);
  const hasCityRole = roles.includes(Role.CITY);
  const hasUserSpaceRole = roles.some(role => role !== Role.AUDITOR && role !== Role.CITY);

  if (hasAuditorRole) spaces.push("auditor");
  if (hasCityRole) spaces.push("city");
  if (hasUserSpaceRole || (!hasAuditorRole && !hasCityRole && roles.length > 0)) spaces.push("user");

  return spaces;
}

/**
 * Determines the default space based on user roles.
 * A default is selected only when the user has access to a single space.
 */
export function determineDefaultSpace(
  hasCityRole: boolean,
  hasSuperAdminRole: boolean,
  hasAuditorRole = false,
  roles: string[] = [],
): UserSpace {
  const availableSpaces = roles.length > 0
    ? getAvailableSpaces(roles)
    : [
        ...(hasAuditorRole ? ["auditor" as const] : []),
        ...(hasCityRole ? ["city" as const] : []),
        ...(!hasCityRole || hasSuperAdminRole ? ["user" as const] : []),
      ];

  return availableSpaces.length === 1 ? availableSpaces[0] : null;
}

/**
 * Updates both the user store and cookie with the selected space
 * @param userStore - The user store instance
 * @param cookie - The cookie instance
 * @param space - The space to set
 */
export function updateSelectedSpace(
  userStore: ReturnType<typeof useUserStore>,
  cookie: ReturnType<typeof useCookie<UserStoreCookie>>,
  space: UserSpace,
): void {
  userStore.setSelectedSpace(space);
  cookie.value = { ...cookie.value, selectedSpace: space };
}
